import { useState, useRef, useEffect } from 'react';
import { Mic, Square, Trash2, Play, Pause, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '@/lib/supabase';

interface AudioRecorderProps {
  onSendAudio: (audioUrl: string) => Promise<boolean>;
  disabled?: boolean;
}

export const AudioRecorder = ({ onSendAudio, disabled = false }: AudioRecorderProps) => {
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'stopped'>('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🔇 Track stopped:', track.kind);
      });
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      console.log('🎤 Requesting microphone access...');

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      streamRef.current = stream;
      console.log('✅ Microphone access granted');

      // Detect supported MIME type
      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav'
      ];

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('✅ Using MIME type:', mimeType);
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream,
        selectedMimeType ? { mimeType: selectedMimeType } : undefined
      );

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log('📦 Chunk received:', event.data.size, 'bytes');
        }
      };

      mediaRecorder.onstop = () => {
        console.log('⏹️ Recording stopped');
        console.log('📦 Total chunks:', chunksRef.current.length);

        if (chunksRef.current.length === 0) {
          console.error('❌ No audio data recorded');
          alert('Erro: Nenhum áudio foi gravado. Tente novamente.');
          stopStream();
          setRecordingState('idle');
          return;
        }

        const blob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm'
        });

        console.log('🎵 Blob created:', blob.size, 'bytes');

        if (blob.size === 0) {
          console.error('❌ Empty blob created');
          alert('Erro: Áudio vazio. Tente novamente.');
          stopStream();
          setRecordingState('idle');
          return;
        }

        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        setRecordingState('stopped');
        stopStream();

        console.log('✅ Audio ready for preview');
      };

      mediaRecorder.onerror = (event: any) => {
        console.error('❌ MediaRecorder error:', event.error);
        alert('Erro na gravação: ' + event.error);
        stopStream();
        setRecordingState('idle');
      };

      mediaRecorder.start(100);
      mediaRecorderRef.current = mediaRecorder;
      setRecordingState('recording');
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log('🔴 Recording started');

    } catch (error: any) {
      console.error('❌ Error starting recording:', error);

      let errorMessage = 'Erro ao acessar o microfone.';

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Permissão negada. Por favor, permita o acesso ao microfone nas configurações do navegador.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'Nenhum microfone encontrado. Conecte um microfone e tente novamente.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = 'Microfone está sendo usado por outro aplicativo.';
      }

      alert(errorMessage);
      setRecordingState('idle');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      console.log('⏸️ Stopping recording...');
      mediaRecorderRef.current.stop();

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    console.log('🗑️ Canceling recording...');

    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.stop();
    }

    stopStream();
    chunksRef.current = [];
    setRecordingState('idle');
    setRecordingTime(0);

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const deleteRecording = () => {
    console.log('🗑️ Deleting recording...');

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioBlob(null);
    setAudioUrl('');
    setRecordingState('idle');
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (!audioElementRef.current) return;

    if (isPlaying) {
      audioElementRef.current.pause();
      setIsPlaying(false);
      console.log('⏸️ Paused');
    } else {
      audioElementRef.current.play();
      setIsPlaying(true);
      console.log('▶️ Playing');
    }
  };

  const sendAudio = async () => {
    if (!audioBlob) {
      alert('Nenhum áudio para enviar');
      return;
    }

    setIsSending(true);

    try {
      console.log('📤 Uploading audio to storage...');

      if (!supabase) {
        alert('Erro: Supabase não configurado');
        return;
      }

      const timestamp = Date.now();
      const fileName = `audio_${timestamp}.ogg`;
      const filePath = `audios/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('chat-audios')
        .upload(filePath, audioBlob, {
          contentType: audioBlob.type || 'audio/ogg',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Storage upload failed:', uploadError);
        alert('Erro ao fazer upload do áudio. Tente novamente.');
        return;
      }

      console.log('✅ Audio uploaded to storage:', uploadData.path);

      const { data: urlData } = supabase
        .storage
        .from('chat-audios')
        .getPublicUrl(filePath);

      const audioUrl = urlData.publicUrl;
      console.log('✅ Public URL generated:', audioUrl);

      console.log('📤 Sending audio...');

      const success = await onSendAudio(audioUrl);

      if (success) {
        console.log('✅ Audio sent successfully');
        deleteRecording();
      } else {
        console.error('❌ Failed to send audio');
        alert('Erro ao enviar áudio. Tente novamente.');
      }

    } catch (error) {
      console.error('❌ Error sending audio:', error);
      alert('Erro ao processar áudio. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // RENDER: Audio Preview Player
  if (recordingState === 'stopped' && audioUrl) {
    return (
      <div className="flex items-center gap-2 w-full max-w-md">
        <audio
          ref={audioElementRef}
          src={audioUrl}
          onEnded={() => {
            setIsPlaying(false);
            console.log('🔚 Audio ended');
          }}
          className="hidden"
        />

        <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-neutral-800 rounded-lg">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={togglePlayPause}
            className="h-8 w-8 p-0 hover:bg-neutral-700 flex-shrink-0"
            disabled={isSending}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-white" />
            ) : (
              <Play className="w-4 h-4 text-white" />
            )}
          </Button>

          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 bg-blue-500 rounded animate-pulse" />
              <div className="w-1 h-4 bg-blue-500 rounded animate-pulse delay-75" />
              <div className="w-1 h-5 bg-blue-500 rounded animate-pulse delay-150" />
              <div className="w-1 h-4 bg-blue-500 rounded animate-pulse delay-75" />
              <div className="w-1 h-3 bg-blue-500 rounded animate-pulse" />
            </div>
            <span className="text-xs text-neutral-400 ml-3">
              {formatTime(recordingTime)}
            </span>
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={deleteRecording}
          disabled={isSending}
          className="h-9 w-9 p-0 hover:bg-red-500/20 flex-shrink-0"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>

        <Button
          type="button"
          size="sm"
          onClick={sendAudio}
          disabled={isSending}
          className="h-9 px-4 bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
        >
          {isSending ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 mr-1" />
              Enviar
            </>
          )}
        </Button>
      </div>
    );
  }

  // RENDER: Recording Mode
  return (
    <div className="flex items-center gap-2">
      {recordingState === 'recording' && (
        <>
          <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 rounded-lg">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-500 text-sm font-mono">{formatTime(recordingTime)}</span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={cancelRecording}
            className="h-9 w-9 p-0 hover:bg-red-500/20"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </>
      )}

      <button
        type="button"
        onClick={recordingState === 'recording' ? stopRecording : startRecording}
        disabled={disabled}
        className={`px-4 py-2 rounded-lg flex items-center justify-center transition-all ${
          disabled
            ? 'bg-neutral-300 cursor-not-allowed opacity-50'
            : recordingState === 'recording'
            ? 'bg-red-500 cursor-pointer hover:bg-red-600'
            : 'bg-[#E4E4E7] cursor-pointer hover:bg-[#D4D4D8]'
        }`}
      >
        {recordingState === 'recording' ? (
          <Square className="w-4 h-4 text-white" />
        ) : (
          <Mic className="w-4 h-4 text-neutral-900" />
        )}
      </button>
    </div>
  );
};
